import React, { useEffect, useRef, useState } from 'react';
import useSWR from 'swr';
import {
 Box,
 Button,
 Flex,
 FormControl,
 FormErrorMessage,
 FormLabel,
 IconButton,
 Input,
 Modal,
 ModalBody,
 ModalCloseButton,
 ModalContent,
 ModalFooter,
 ModalHeader,
 ModalOverlay,
 Stack,
 Text,
 Textarea,
 Tooltip,
 useToast,
} from '@chakra-ui/react';
import { FiDownload, FiEdit2, FiFileText, FiLink, FiTrash2, FiUpload } from 'react-icons/fi';
import baseService from '../../services/base';
import { fetchData } from '../../services/fetch';
import VideoFrame from '../../components/Data/VideoFrame';
import { getFileIcon } from '../../helpers/getFileIcon';
import { Select } from '../../components/Input';
import { CATEGORY_OPTIONS } from './categories';
import { decodificarNomeDeUpload } from '../../utils/nome-de-upload';
import { hostDoLink, isHttpsUrl } from '../../helpers/externalLink';
import SelecaoMultipla from '../../components/Catalog/SelecaoMultipla';

const EMPTY_LINK = { label: '', url: '' };

const anoAtual = new Date().getFullYear();

const EMPTY = {
 title: '',
 description: '',
 youtube_url: '',
 exercise: String(anoAtual),
 category: '',
 id_company_groups: [],
};

const YOUTUBE_URL_REGEX = /^https?:\/\/(www\.)?(youtube\.com|youtu\.be)\//i;

/**
 * Criação e edição de conteúdo educativo ("Aulas e Materiais"). Sem exclusão
 * de propósito - o backend não expõe DELETE porque conteúdo de ano anterior
 * nunca é removido nem substituído.
 */
export default function EducationalContentModal({ isOpen, onClose, content, onSaved }) {
 const toast = useToast();
 const fileInputRef = useRef(null);
 const [form, setForm] = useState(EMPTY);
 const [errors, setErrors] = useState({});
 const [isSaving, setIsSaving] = useState(false);
 // Na criação o conteúdo ainda não tem id — material escolhido fica
 // pendente aqui e só sobe depois que o POST principal volta com o id
 // (mesmo padrão do ActivityModal.tsx de cronogramas no cliente).
 const [pendingFiles, setPendingFiles] = useState([]);
 const [isUploadingMaterial, setIsUploadingMaterial] = useState(false);
 // EG-70 — botões de link externo. Edição: cada ação vai direto pra API.
 // Criação: ficam aqui pendentes e sobem depois do POST (igual materiais).
 const [links, setLinks] = useState([]);
 const [linkForm, setLinkForm] = useState(EMPTY_LINK);
 const [editingLinkKey, setEditingLinkKey] = useState(null);
 const [linkError, setLinkError] = useState('');
 const [isSavingLink, setIsSavingLink] = useState(false);

 const isEditing = !!content?.id_educational_content;

 // Na edição o id já existe: material anexado sobe na hora, sem estado
 // pendente. Aberto/fechado do modal entra na key pra não vazar dado de
 // um conteúdo pro outro quando o admin troca de edição sem fechar/abrir.
 const { data: materials, mutate: mutateMaterials } = useSWR(
  isOpen && isEditing ? `educational-content/${content.id_educational_content}/media` : null,
  fetchData
 );

 const { data: companyGroups } = useSWR(isOpen ? '/document/company-groups' : null, fetchData);
 const companyOptions = (companyGroups || []).map((group) => ({
  value: String(group.id_company_group),
  label: group.company_name || `Grupo ${group.id_company_group}`,
 }));

 useEffect(() => {
  if (!isOpen) return;
  setErrors({});
  setPendingFiles([]);
  setLinks(content?.links || []);
  setLinkForm(EMPTY_LINK);
  setEditingLinkKey(null);
  setLinkError('');
  setForm(
   content
    ? {
       title: content.title || '',
       description: content.description || '',
       youtube_url: content.youtube_url || '',
       exercise: String(content.exercise ?? anoAtual),
       category: content.category || '',
       id_company_groups: (content.company_groups || []).map(String),
      }
    : EMPTY
  );
 }, [isOpen, content]);

 function resetLinkForm() {
  setLinkForm(EMPTY_LINK);
  setEditingLinkKey(null);
  setLinkError('');
 }

 function startEditLink(link, index) {
  setLinkForm({ label: link.label, url: link.url });
  setEditingLinkKey(isEditing ? link.id_educational_content_link : index);
  setLinkError('');
 }

 async function handleSaveLink() {
  const payload = { label: linkForm.label.trim(), url: linkForm.url.trim() };
  if (!payload.label) return setLinkError('Informe o texto do botão');
  if (!isHttpsUrl(payload.url)) return setLinkError('Informe um link que comece com https://');

  if (!isEditing) {
   setLinks((prev) =>
    editingLinkKey === null
     ? [...prev, payload]
     : prev.map((link, index) => (index === editingLinkKey ? payload : link))
   );
   resetLinkForm();
   return;
  }

  const base = `educational-content/${content.id_educational_content}/links`;
  setIsSavingLink(true);
  try {
   if (editingLinkKey === null) {
    const { data } = await baseService.post(base, payload);
    setLinks((prev) => [...prev, data]);
   } else {
    const { data } = await baseService.patch(`${base}/${editingLinkKey}`, payload);
    setLinks((prev) =>
     prev.map((link) => (link.id_educational_content_link === editingLinkKey ? data : link))
    );
   }
   resetLinkForm();
   onSaved();
  } catch (error) {
   toast({
    title: 'Não foi possível salvar o link',
    description: error?.response?.data?.message,
    status: 'error',
   });
  } finally {
   setIsSavingLink(false);
  }
 }

 async function handleRemoveLink(link, index) {
  if (!isEditing) {
   setLinks((prev) => prev.filter((_, i) => i !== index));
   return;
  }
  try {
   await baseService.delete(
    `educational-content/${content.id_educational_content}/links/${link.id_educational_content_link}`
   );
   setLinks((prev) =>
    prev.filter((l) => l.id_educational_content_link !== link.id_educational_content_link)
   );
   onSaved();
  } catch (error) {
   toast({
    title: 'Não foi possível remover o link',
    description: error?.response?.data?.message,
    status: 'error',
   });
  }
 }

 async function uploadMaterial(idEducationalContent, file) {
  const formData = new FormData();
  formData.append('file', file);
  await baseService.post(`educational-content/${idEducationalContent}/media`, formData, {
   headers: { 'Content-Type': 'multipart/form-data' },
  });
 }

 async function handleFileChosen(event) {
  const file = event.target.files?.[0];
  if (fileInputRef.current) fileInputRef.current.value = '';
  if (!file) return;

  if (!isEditing) {
   setPendingFiles((prev) => [...prev, file]);
   return;
  }

  setIsUploadingMaterial(true);
  try {
   await uploadMaterial(content.id_educational_content, file);
   mutateMaterials();
  } catch (error) {
   toast({
    title: 'Não foi possível anexar o arquivo',
    description: error?.response?.data?.message,
    status: 'error',
   });
  } finally {
   setIsUploadingMaterial(false);
  }
 }

 function removePendingFile(index) {
  setPendingFiles((prev) => prev.filter((_, i) => i !== index));
 }

 async function handleRemoveMaterial(idMedia) {
  try {
   await baseService.delete(
    `educational-content/${content.id_educational_content}/media/${idMedia}`
   );
   mutateMaterials();
  } catch (error) {
   toast({
    title: 'Não foi possível remover o material',
    description: error?.response?.data?.message,
    status: 'error',
   });
  }
 }

 async function handleDownloadMaterial(idMedia) {
  const { data } = await baseService.get(
   `educational-content/${content.id_educational_content}/media/${idMedia}/download-url`
  );
  window.open(data.url, '_blank');
 }

 function setField(field, value) {
  setForm((prev) => ({ ...prev, [field]: value }));
  setErrors((prev) => ({ ...prev, [field]: undefined }));
 }

 function validate() {
  const next = {};
  if (!form.title.trim()) next.title = 'Informe o título';
  if (!form.youtube_url.trim()) {
   next.youtube_url = 'Informe o link do YouTube';
  } else if (!YOUTUBE_URL_REGEX.test(form.youtube_url.trim())) {
   next.youtube_url = 'O link precisa ser do YouTube (youtube.com ou youtu.be)';
  }
  if (!form.exercise || Number.isNaN(Number(form.exercise))) {
   next.exercise = 'Informe o ano';
  }
  setErrors(next);
  return Object.keys(next).length === 0;
 }

 async function handleSubmit() {
  if (!validate()) return;

  // Link digitado nos campos mas sem clicar em "Adicionar link" entra junto
  // com o salvar — antes ele era descartado em silêncio enquanto o conteúdo
  // salvava com sucesso. Inválido: para tudo e mostra o erro no campo.
  const typedLink = { label: linkForm.label.trim(), url: linkForm.url.trim() };
  const hasTypedLink = !!(typedLink.label || typedLink.url);
  if (hasTypedLink) {
   if (!typedLink.label) return setLinkError('Informe o texto do botão');
   if (!isHttpsUrl(typedLink.url)) return setLinkError('Informe um link que comece com https://');
  }

  const payload = {
   title: form.title.trim(),
   description: form.description.trim() || undefined,
   youtube_url: form.youtube_url.trim(),
   exercise: Number(form.exercise),
   // null (não undefined) pra edição conseguir limpar o campo.
   category: form.category || null,
   // Vazio = todas as empresas (o backend troca o conjunto inteiro).
   id_company_groups: form.id_company_groups.map(Number),
  };

  setIsSaving(true);
  try {
   let saved;
   if (isEditing) {
    const { data } = await baseService.patch(
     `educational-content/${content.id_educational_content}`,
     payload
    );
    saved = data;
   } else {
    const { data } = await baseService.post('educational-content', payload);
    saved = data;
   }

   if (!isEditing && pendingFiles.length > 0) {
    for (const file of pendingFiles) {
     try {
      await uploadMaterial(saved.id_educational_content, file);
     } catch (uploadError) {
      toast({
       title: `Não foi possível anexar "${file.name}"`,
       description: uploadError?.response?.data?.message,
       status: 'warning',
      });
     }
    }
   }

   const linksBase = `educational-content/${saved.id_educational_content}/links`;
   // Criação: os pendentes + o digitado (substituindo o que estava em edição).
   const linksToCreate = isEditing
    ? []
    : hasTypedLink
    ? editingLinkKey === null
      ? [...links, typedLink]
      : links.map((link, index) => (index === editingLinkKey ? typedLink : link))
    : links;
   // Edição: só o digitado (o resto já foi direto pra API).
   if (isEditing && hasTypedLink) {
    if (editingLinkKey === null) linksToCreate.push(typedLink);
    else {
     try {
      await baseService.patch(`${linksBase}/${editingLinkKey}`, typedLink);
     } catch (linkError) {
      toast({
       title: `Não foi possível salvar o link "${typedLink.label}"`,
       description: linkError?.response?.data?.message,
       status: 'warning',
      });
     }
    }
   }
   for (const link of linksToCreate) {
    try {
     await baseService.post(linksBase, link);
    } catch (linkError) {
     toast({
      title: `Não foi possível criar o link "${link.label}"`,
      description: linkError?.response?.data?.message,
      status: 'warning',
     });
    }
   }

   toast({
    title: isEditing ? 'Conteúdo atualizado' : 'Conteúdo cadastrado',
    status: 'success',
   });
   await onSaved();
   onClose();
  } catch (error) {
   const message = error?.response?.data?.message;
   toast({
    title: 'Não foi possível salvar o conteúdo',
    description: Array.isArray(message) ? message[0] : message,
    status: 'error',
   });
  } finally {
   setIsSaving(false);
  }
 }

 return (
  <Modal isOpen={isOpen} onClose={onClose} isCentered size='xl'>
   <ModalOverlay />
   <ModalContent>
    <ModalHeader>{isEditing ? 'Editar conteúdo' : 'Novo conteúdo'}</ModalHeader>
    <ModalCloseButton />

    <ModalBody>
     <Stack spacing='16px'>
      <FormControl isInvalid={!!errors.title} isRequired>
       <FormLabel fontSize='sm'>Título</FormLabel>
       <Input
        placeholder='Como preencher os indicadores GRI'
        value={form.title}
        onChange={(e) => setField('title', e.target.value)}
       />
       <FormErrorMessage>{errors.title}</FormErrorMessage>
      </FormControl>

      <FormControl>
       <FormLabel fontSize='sm'>Descrição</FormLabel>
       <Textarea
        rows={3}
        placeholder='Do que trata o vídeo (opcional)'
        value={form.description}
        onChange={(e) => setField('description', e.target.value)}
       />
      </FormControl>

      <FormControl isInvalid={!!errors.youtube_url} isRequired>
       <FormLabel fontSize='sm'>Link do YouTube</FormLabel>
       <Input
        placeholder='https://www.youtube.com/watch?v=xxxxxxxxxxx'
        value={form.youtube_url}
        onChange={(e) => setField('youtube_url', e.target.value)}
       />
       <FormErrorMessage>{errors.youtube_url}</FormErrorMessage>
       {/* Preview ao vivo — confere se o link colado é o vídeo certo sem
           precisar sair da tela (pedido do Lucas, 22/09). */}
       {YOUTUBE_URL_REGEX.test(form.youtube_url.trim()) && (
        <Box mt='12px' borderRadius='8px' overflow='hidden' bg='black' aspectRatio='16 / 9'>
         <VideoFrame videoUrl={form.youtube_url.trim()} width='100%' height='100%' />
        </Box>
       )}
      </FormControl>

      <FormControl isInvalid={!!errors.exercise} isRequired>
       <FormLabel fontSize='sm'>Exercício/ano</FormLabel>
       <Input
        type='number'
        placeholder={String(anoAtual)}
        value={form.exercise}
        onChange={(e) => setField('exercise', e.target.value)}
       />
       <FormErrorMessage>{errors.exercise}</FormErrorMessage>
      </FormControl>

      <Select
       name='category'
       label='Categoria'
       labelFontSize='sm'
       value={form.category}
       onChange={(e) => setField('category', e.target.value)}
       options={[{ value: '', label: 'Selecione' }, ...CATEGORY_OPTIONS]}
      />

      {/* Exclusivo = só as empresas marcadas veem (o filtro é no backend). */}
      <FormControl>
       <FormLabel fontSize='sm'>Disponível para</FormLabel>
       <SelecaoMultipla
        options={companyOptions}
        value={form.id_company_groups}
        onChange={(valores) => setField('id_company_groups', valores)}
        placeholder='Todas as empresas'
        searchPlaceholder='Buscar empresa'
        countLabel={(n) => `${n} empresa${n > 1 ? 's' : ''}`}
       />
       <Text fontSize='xs' color='#818A8F' mt='6px'>
        Nenhuma empresa marcada = o conteúdo aparece para todas.
       </Text>
      </FormControl>

      <FormControl>
       <FormLabel fontSize='sm'>Materiais</FormLabel>
       <Stack spacing='8px'>
        {isEditing &&
         materials?.map((material) => (
          <Flex
           key={material.id_educational_content_media}
           align='center'
           justify='space-between'
           gap='8px'
           p='8px 12px'
           borderRadius='8px'
           border='1px solid #E7E9EA'
          >
           <Flex align='center' gap='8px' minW={0}>
            {getFileIcon(material.name) ? (
             <img src={getFileIcon(material.name)} alt='' width='20' height='20' />
            ) : (
             <FiFileText color='#818A8F' />
            )}
            <Text fontSize='sm' noOfLines={1}>
             {decodificarNomeDeUpload(material.name)}
            </Text>
           </Flex>
           <Flex gap='4px'>
            <Tooltip label='Baixar'>
             <IconButton
              aria-label='Baixar material'
              icon={<FiDownload />}
              size='xs'
              variant='ghost'
              onClick={() => handleDownloadMaterial(material.id_media)}
             />
            </Tooltip>
            <Tooltip label='Remover'>
             <IconButton
              aria-label='Remover material'
              icon={<FiTrash2 />}
              size='xs'
              variant='ghost'
              colorScheme='red'
              onClick={() => handleRemoveMaterial(material.id_media)}
             />
            </Tooltip>
           </Flex>
          </Flex>
         ))}

        {!isEditing &&
         pendingFiles.map((file, index) => (
          <Flex
           key={`${file.name}-${index}`}
           align='center'
           justify='space-between'
           gap='8px'
           p='8px 12px'
           borderRadius='8px'
           border='1px solid #E7E9EA'
          >
           <Flex align='center' gap='8px' minW={0}>
            {getFileIcon(file.name) ? (
             <img src={getFileIcon(file.name)} alt='' width='20' height='20' />
            ) : (
             <FiFileText color='#818A8F' />
            )}
            <Text fontSize='sm' noOfLines={1}>
             {file.name}
            </Text>
           </Flex>
           <Tooltip label='Remover'>
            <IconButton
             aria-label='Remover material'
             icon={<FiTrash2 />}
             size='xs'
             variant='ghost'
             colorScheme='red'
             onClick={() => removePendingFile(index)}
            />
           </Tooltip>
          </Flex>
         ))}

        {isEditing && materials?.length === 0 && (
         <Text fontSize='xs' color='#818A8F'>
          Nenhum material anexado ainda.
         </Text>
        )}
        {!isEditing && pendingFiles.length === 0 && (
         <Text fontSize='xs' color='#818A8F'>
          Materiais escolhidos aqui sobem junto quando o conteúdo for salvo.
         </Text>
        )}

        <input
         type='file'
         ref={fileInputRef}
         style={{ display: 'none' }}
         onChange={handleFileChosen}
        />
        <Button
         size='sm'
         variant='outline'
         leftIcon={<FiUpload />}
         isLoading={isUploadingMaterial}
         onClick={() => fileInputRef.current?.click()}
         alignSelf='flex-start'
        >
         Anexar arquivo
        </Button>
       </Stack>
      </FormControl>

      {/* EG-70 — botão de link externo (ex.: pasta do Drive): o cliente vê
          um botão azul com o texto escrito aqui. */}
      <FormControl isInvalid={!!linkError}>
       <FormLabel fontSize='sm'>Links</FormLabel>
       <Stack spacing='8px'>
        {links.map((link, index) => (
         <Flex
          key={link.id_educational_content_link ?? `pendente-${index}`}
          align='center'
          justify='space-between'
          gap='8px'
          p='8px 12px'
          borderRadius='8px'
          border='1px solid #E7E9EA'
         >
          <Flex align='center' gap='8px' minW={0}>
           <FiLink color='#2F80ED' />
           <Box minW={0}>
            <Text fontSize='sm' fontWeight={600} noOfLines={1}>
             {link.label}
            </Text>
            <Text fontSize='xs' color='#818A8F' noOfLines={1}>
             {hostDoLink(link.url)}
            </Text>
           </Box>
          </Flex>
          <Flex gap='4px'>
           <Tooltip label='Editar'>
            <IconButton
             aria-label='Editar link'
             icon={<FiEdit2 />}
             size='xs'
             variant='ghost'
             onClick={() => startEditLink(link, index)}
            />
           </Tooltip>
           <Tooltip label='Remover'>
            <IconButton
             aria-label='Remover link'
             icon={<FiTrash2 />}
             size='xs'
             variant='ghost'
             colorScheme='red'
             onClick={() => handleRemoveLink(link, index)}
            />
           </Tooltip>
          </Flex>
         </Flex>
        ))}

        <Flex gap='8px' wrap='wrap'>
         <Input
          size='sm'
          flex='1'
          minW='140px'
          maxLength={80}
          placeholder='Texto do botão'
          value={linkForm.label}
          onChange={(e) => setLinkForm((prev) => ({ ...prev, label: e.target.value }))}
         />
         <Input
          size='sm'
          flex='2'
          minW='200px'
          placeholder='https://drive.google.com/...'
          value={linkForm.url}
          onChange={(e) => setLinkForm((prev) => ({ ...prev, url: e.target.value }))}
         />
        </Flex>
        <FormErrorMessage mt='0'>{linkError}</FormErrorMessage>
        <Flex gap='8px'>
         <Button
          size='sm'
          variant='outline'
          leftIcon={<FiLink />}
          isLoading={isSavingLink}
          onClick={handleSaveLink}
         >
          {editingLinkKey === null ? 'Adicionar link' : 'Salvar link'}
         </Button>
         {editingLinkKey !== null && (
          <Button size='sm' variant='ghost' onClick={resetLinkForm}>
           Cancelar
          </Button>
         )}
        </Flex>
        <Text fontSize='xs' color='#818A8F'>
         Quem acessa o destino é definido no próprio link (ex.: no Drive, "qualquer pessoa com o link").
        </Text>
       </Stack>
      </FormControl>
     </Stack>
    </ModalBody>

    <ModalFooter gap='12px'>
     <Button variant='cancel' onClick={onClose}>
      Cancelar
     </Button>
     <Button variant='primary' isLoading={isSaving} onClick={handleSubmit}>
      Salvar
     </Button>
    </ModalFooter>
   </ModalContent>
  </Modal>
 );
}
